import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertTriangle, TrendingUp } from "lucide-react";

// Overload Sales Dashboard — grid Sat/Sun 12 bulan ke depan dgn kapasitas per divisi
const CAPACITY = {
  "div-mua": 3,
  "div-decor": 4,
  "div-photo": 5,
  "div-snap": 3,
  "div-music": 3,
  "div-warawiri": 3,
  "div-wo": 3,
};
const DIV_LABEL = {
  "div-mua": "MUA",
  "div-decor": "Decor",
  "div-photo": "Photo/Video (PBH)",
  "div-snap": "Historia Snap",
  "div-music": "Historia Musical",
  "div-warawiri": "Wara Wiri",
  "div-wo": "Wedding Organizer",
};

export default function OverloadPlanner() {
  const [bookings, setBookings] = useState([]);
  const [division, setDivision] = useState("div-mua");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/bookings").then(({ data }) => { setBookings(data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const cap = CAPACITY[division] || 3;
  // Build 12 months forward Sat/Sun map
  const weekends = useMemo(() => {
    const items = [];
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const end = new Date(now); end.setMonth(end.getMonth() + 12);
    for (let d = new Date(now); d <= end; d.setDate(d.getDate() + 1)) {
      const day = d.getDay();
      if (day === 0 || day === 6) {
        items.push({ date: d.toISOString().slice(0, 10), day: day === 0 ? "Min" : "Sab", weekday: day });
      }
    }
    return items;
  }, []);

  const counts = useMemo(() => {
    const map = {};
    bookings.forEach((b) => {
      if (b.status === "Cancelled") return;
      if (!b.event_date) return;
      if (division && !(b.services || []).some((s) => s.division_id === division)) return;
      map[b.event_date] = (map[b.event_date] || 0) + 1;
    });
    return map;
  }, [bookings, division]);

  const byMonth = useMemo(() => {
    const groups = {};
    const monthNames = ["Januari","Februari","Maret","April","Mei","Juni","Juli","Agustus","September","Oktober","November","Desember"];
    weekends.forEach((w) => {
      const dt = new Date(w.date);
      const key = `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}`;
      const label = `${monthNames[dt.getMonth()]} ${dt.getFullYear()}`;
      if (!groups[key]) groups[key] = { label, days: [] };
      groups[key].days.push({ ...w, count: counts[w.date] || 0 });
    });
    return Object.entries(groups);
  }, [weekends, counts]);

  const toneOf = (count) => {
    if (count >= cap) return "bg-rose-100 text-rose-800 border-rose-300";
    if (count >= Math.max(1, cap - 1)) return "bg-amber-100 text-amber-800 border-amber-300";
    if (count > 0) return "bg-sky-50 text-sky-700 border-sky-200";
    return "bg-emerald-50 text-emerald-700 border-emerald-200";
  };
  const labelOf = (count) => {
    if (count >= cap) return "Penuh";
    if (count >= Math.max(1, cap - 1)) return "Hampir Penuh";
    if (count > 0) return "Ada Slot";
    return "Kosong";
  };

  const total = weekends.length;
  const full = weekends.filter((w) => (counts[w.date] || 0) >= cap).length;
  const almost = weekends.filter((w) => { const c = counts[w.date] || 0; return c > 0 && c < cap; }).length;
  const empty = total - full - almost;

  return (
    <div className="space-y-6" data-testid="overload-planner-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Peta Kapasitas Sabtu-Minggu (12 Bulan)</h1>
          <p className="text-sm text-[#5C6066] mt-1">Sales bisa langsung lihat tanggal mana yang masih aman dijual sesuai kapasitas divisi.</p>
        </div>
        <Select value={division} onValueChange={setDivision}>
          <SelectTrigger className="w-60" data-testid="overload-div-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(DIV_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v} — kapasitas {CAPACITY[k]}/hari</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 border border-[#E5E0D8] bg-white shadow-none" data-testid="ov-kpi-total"><div className="text-[10px] uppercase tracking-wider text-[#8C9096] font-semibold">Total Sabtu-Minggu 12 Bulan</div><div className="font-display text-2xl font-bold mt-1">{total}</div></Card>
        <Card className="p-4 border border-emerald-200 bg-emerald-50 shadow-none" data-testid="ov-kpi-empty"><div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Kosong (Aman Dijual)</div><div className="font-display text-2xl font-bold mt-1 text-emerald-800">{empty}</div></Card>
        <Card className="p-4 border border-amber-200 bg-amber-50 shadow-none" data-testid="ov-kpi-almost"><div className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold">Terisi Sebagian</div><div className="font-display text-2xl font-bold mt-1 text-amber-800">{almost}</div></Card>
        <Card className="p-4 border border-rose-200 bg-rose-50 shadow-none" data-testid="ov-kpi-full"><div className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold">Penuh ({DIV_LABEL[division]})</div><div className="font-display text-2xl font-bold mt-1 text-rose-800">{full}</div></Card>
      </div>

      <Card className="border border-[#E5E0D8] bg-white shadow-none p-4">
        <div className="flex flex-wrap gap-2 mb-3 text-[10px]">
          <span className="px-2 py-1 rounded border bg-emerald-50 text-emerald-700 border-emerald-200">Kosong</span>
          <span className="px-2 py-1 rounded border bg-sky-50 text-sky-700 border-sky-200">Ada Slot (1..{cap-1})</span>
          <span className="px-2 py-1 rounded border bg-amber-100 text-amber-800 border-amber-300">Hampir Penuh</span>
          <span className="px-2 py-1 rounded border bg-rose-100 text-rose-800 border-rose-300">Penuh (≥{cap})</span>
        </div>
        <div className="space-y-4">
          {loading && <div className="text-center py-6 text-[#8C9096] text-sm">Memuat...</div>}
          {!loading && byMonth.map(([key, grp]) => (
            <div key={key} data-testid={`ov-month-${key}`}>
              <div className="text-xs font-semibold uppercase tracking-wider text-[#B7A175] mb-2">{grp.label}</div>
              <div className="flex flex-wrap gap-1.5">
                {grp.days.map((day) => (
                  <Link key={day.date} to={`/calendar`} title={`${day.date} — ${day.count} acara`} className={`px-2 py-1.5 rounded border ${toneOf(day.count)} min-w-[76px] text-center hover:shadow-sm transition-shadow`} data-testid={`ov-day-${day.date}`}>
                    <div className="text-[9px] font-semibold">{day.day} · {day.date.slice(8)}</div>
                    <div className="text-[10px] font-bold mt-0.5">{day.count}/{cap}</div>
                    <div className="text-[8px] opacity-80">{labelOf(day.count)}</div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
